import React, { ReactNode, SyntheticEvent } from "react";
import cx from "classnames";
import stylesV2 from "./TextInput.module.scss";
import stylesV3 from "./TextInputV2.module.scss";
import { useSchedulingABVariant } from "../../features/scheduling/utils/useSchedulingABVariant";
import errorIcon from "images/error.svg";
import { GuidedTooltip } from "components/GuidedTooltip";

type InputMode = "numeric";
type AutoComplete =
  | "username"
  | "new-password"
  | "current-password"
  | "one-time-code";
type InputType = "text" | "tel" | "email" | "password" | "date";

export interface TextInputProps {
  inputType: InputType;
  autoComplete?: AutoComplete;
  name: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: SyntheticEvent) => void;
  placeholder?: string;
  errorMessage?: string;
  children?: ReactNode;
  defaultValue?: string;
  inputMode?: InputMode;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  readOnly?: boolean;
  afterInput?: React.ReactNode;
  maxLength?: number;
  ariaLabel?: string;
  guidedTooltip?: React.ReactNode;
  guidedTooltipClassName?: string;
  showError?: boolean;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      inputType,
      autoComplete,
      autoFocus,
      name,
      onBlur,
      onChange,
      onClick,
      placeholder,
      errorMessage,
      children,
      defaultValue,
      inputMode,
      className,
      errorClassName,
      readOnly,
      afterInput,
      maxLength,
      ariaLabel,
      guidedTooltip,
      guidedTooltipClassName,
    },
    ref
  ) => {
    const styles = useSchedulingABVariant().isV3 ? stylesV3 : stylesV2;
    const [tooltipVisible, setTooltipVisible] = React.useState(false);
    const errorToDisplay = errorMessage ? (
      `Error: ${errorMessage}`
    ) : (
      <span>&nbsp;</span>
    );

    return (
      <div className={cx(styles.container, className)}>
        <label className={styles.label} htmlFor={name}>
          {children}
        </label>
        <div className={styles.inputContainer}>
          <input
            pattern="\S+"
            required
            autoFocus={autoFocus}
            type={inputType}
            inputMode={inputMode}
            className={cx(styles.input, {
              [styles.error]: errorMessage !== undefined,
            })}
            name={name}
            autoComplete={autoComplete}
            id={name}
            onFocus={() => setTooltipVisible(true)}
            onBlur={(e) => {
              setTooltipVisible(false);
              return onBlur && onBlur(e);
            }}
            onChange={onChange}
            onClick={onClick}
            placeholder={placeholder}
            ref={ref}
            defaultValue={defaultValue}
            aria-invalid={errorMessage !== undefined && errorMessage !== null}
            readOnly={readOnly}
            maxLength={maxLength}
            aria-label={ariaLabel}
          />
          {afterInput}
          {errorMessage && (
            <img className={styles.errorIcon} alt="" src={errorIcon} />
          )}
        </div>
        {guidedTooltip && tooltipVisible && (
          <div
            className={cx(styles.tooltip, guidedTooltipClassName, {
              [styles.tooltipAfterError]: !!errorMessage,
            })}
          >
            <GuidedTooltip>{guidedTooltip}</GuidedTooltip>
          </div>
        )}
        <p className={cx(styles.errorMessage, errorClassName)}>
          {errorToDisplay}
        </p>
      </div>
    );
  }
);

export const ApptTextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      inputType,
      autoComplete,
      autoFocus,
      name,
      onBlur,
      onChange,
      onClick,
      placeholder,
      errorMessage,
      children,
      defaultValue,
      inputMode,
      className,
      inputClassName,
      errorClassName,
      readOnly,
      afterInput,
      maxLength,
      ariaLabel,
      guidedTooltip,
      guidedTooltipClassName,
    },
    ref
  ) => {
    const styles = useSchedulingABVariant().isV3 ? stylesV3 : stylesV2;
    const [tooltipVisible, setTooltipVisible] = React.useState(false);
    const errorToDisplay = errorMessage ? (
      `Error: ${errorMessage}`
    ) : (
      <span>&nbsp;</span>
    );

    return (
      <div className={cx(styles.apptContainer, className)}>
        <label className={styles.label} htmlFor={name}>
          {children}
        </label>
        <div className={styles.inputContainer}>
          <input
            pattern="\S+"
            required
            autoFocus={autoFocus}
            type={inputType}
            inputMode={inputMode}
            className={cx(inputClassName || styles.input, {
              [styles.error]: errorMessage !== undefined,
            })}
            name={name}
            autoComplete={autoComplete}
            id={name}
            onFocus={() => setTooltipVisible(true)}
            onBlur={(e) => {
              setTooltipVisible(false);
              return onBlur && onBlur(e);
            }}
            onChange={onChange}
            onClick={onClick}
            placeholder={placeholder}
            ref={ref}
            defaultValue={defaultValue}
            aria-invalid={errorMessage !== undefined && errorMessage !== null}
            readOnly={readOnly}
            maxLength={maxLength}
            aria-label={ariaLabel}
          />
          {afterInput}
          {errorMessage && (
            <img className={styles.errorIcon} alt="" src={errorIcon} />
          )}
        </div>
        {guidedTooltip && tooltipVisible && (
          <div
            className={cx(styles.tooltip, guidedTooltipClassName, {
              [styles.tooltipAfterError]: !!errorMessage,
            })}
          >
            <GuidedTooltip>{guidedTooltip}</GuidedTooltip>
          </div>
        )}
        <p className={cx(styles.errorMessage, errorClassName)}>
          {errorToDisplay}
        </p>
      </div>
    );
  }
);


export const CiamTextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      inputType,
      autoComplete,
      autoFocus,
      name,
      onBlur,
      onChange,
      onClick,
      placeholder,
      errorMessage,
      children,
      defaultValue,
      inputMode,
      className,
      errorClassName,
      readOnly,
      afterInput,
      maxLength,
      ariaLabel,
      guidedTooltip,
      guidedTooltipClassName,
      showError,
    },
    ref,
  ) => {
    const styles = useSchedulingABVariant().isV3 ? stylesV3 : stylesV2;
    const [tooltipVisible, setTooltipVisible] = React.useState(false);
    const errorToDisplay = errorMessage ? (
      `${errorMessage}`
    ) : (
      <span>&nbsp;</span>
    );

    return (
      <div className={cx(styles.caimContainer, className)}>
        <label className={styles.label} htmlFor={name}>
          {children}
        </label>
        <div className={styles.inputContainer}>
          <input
            pattern="\S+"
            required
            autoFocus={autoFocus}
            type={inputType}
            inputMode={inputMode}
            className={cx(styles.input, {
              [styles.ciam_error]: errorMessage !== undefined,
            })}
            name={name}
            autoComplete={autoComplete}
            id={name}
            onFocus={() => setTooltipVisible(true)}
            onBlur={(e) => {
              setTooltipVisible(false);
              return onBlur && onBlur(e);
            }}
            onChange={onChange}
            onClick={onClick}
            placeholder={placeholder}
            ref={ref}
            defaultValue={defaultValue}
            aria-invalid={errorMessage !== undefined && errorMessage !== null}
            readOnly={readOnly}
            maxLength={maxLength}
            aria-label={ariaLabel}
          />
          {afterInput}
        </div>
        {guidedTooltip && tooltipVisible && (
          <div
            className={cx(styles.tooltip, guidedTooltipClassName, {
              [styles.tooltipAfterError]: !!errorMessage,
            })}
          >
            <GuidedTooltip>{guidedTooltip}</GuidedTooltip>
          </div>
        )}
        {showError && errorMessage && (
          <p className={cx(styles.errorMessageCIAM, errorClassName)}>
            {errorToDisplay}
          </p>
        )}
      </div>
    );
  }
);